class AnnouncementBar extends HTMLElement {
    connectedCallback() {
        this.wrapper = this.querySelector('.announcement-bar');
        this.track = this.querySelector('.announcement-track');
        this.refreshContents();
        this.swiperContainer = this.querySelector('.announcement-track-container.swiper');

        if (!this.wrapper || !this.track || !this.primaryContent) return;

        this.updateTickers = this.updateTickers.bind(this);
        this.startActiveTicker = this.startActiveTicker.bind(this);
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.onResize = () => {
            const startActive = this.swiperInstance ? false : true;
            this.updateTickers({ startActive, force: true });
        };

        if (this.swiperContainer) {
            if (window.Swiper) {
                this.initSwiper();
            } else {
                this.swiperInitAttempts = 0;
                this.waitForSwiper();
            }
        } else {
            this.resizeObserver = new ResizeObserver(this.onResize);

            requestAnimationFrame(() => {
                this.updateTickers({ startActive: true, force: true });
                this.resizeObserver.observe(this.wrapper);
            });
        }

        this.updateHeaderHeight();
    }

    updateTickers(options = {}) {
        const { startActive = false, force = false } = options;

        this.refreshContents();

        if (!this.contents || this.contents.length === 0) return;

        this.contents.forEach((content) => {
            this.applyTicker(content, { force });
        });

        this.updateHeaderHeight();

        if (startActive) {
            this.startActiveTicker();
        }
    }

    applyTicker(content, { force = false } = {}) {
        if (!content) return;

        if (!this.hasAttribute('auto-ticker')) {
            this.resetTicker(content);
            return;
        }

        const textWrapper = content.querySelector('.announcement-text');
        const inner = textWrapper ? textWrapper.querySelector('.announcement-text__inner:not(.announcement-text__inner--clone)') : null;

        if (!textWrapper || !inner) return;

        const containerWidth = this.wrapper ? this.wrapper.offsetWidth : content.offsetWidth;
        const textWidth = inner.scrollWidth;
        const previousWidth = parseFloat(content.dataset.tickerWidth || '0');
        const widthChanged = Math.abs(previousWidth - textWidth) > 1;
        const needsTicker = textWidth > (containerWidth - 48);

        if (!needsTicker) {
            this.resetTicker(content);
            return;
        }

        const shouldRebuild = force || widthChanged || !content.classList.contains('ticker');

        if (shouldRebuild) {
            this.resetTicker(content, { preserveClone: false });
        }

        let cloneExists = !!textWrapper.querySelector('.announcement-text__inner--clone');
        if (!cloneExists) {
            const clone = inner.cloneNode(true);
            clone.classList.add('announcement-text__inner--clone');
            textWrapper.appendChild(clone);
        }

        const duration = this.getTickerDuration(content);

        content.classList.add('ticker');
        content.classList.remove('ticker-running');
        textWrapper.style.setProperty('--ticker-width', `${textWidth}px`);
        if (duration) {
            textWrapper.style.setProperty('--ticker-duration', duration);
        } else {
            textWrapper.style.removeProperty('--ticker-duration');
        }
        textWrapper.style.setProperty('transform', 'translateX(0)');
        content.dataset.tickerWidth = textWidth;
    }

    resetTicker(content, { preserveClone = false } = {}) {
        if (!content) return;

        const textWrapper = content.querySelector('.announcement-text');
        if (!textWrapper) return;

        if (!preserveClone) {
            const clones = textWrapper.querySelectorAll('.announcement-text__inner--clone');
            clones.forEach((clone) => clone.remove());
        }

        content.classList.remove('ticker');
        content.classList.remove('ticker-running');
        textWrapper.style.removeProperty('--ticker-width');
        textWrapper.style.removeProperty('--ticker-duration');
        textWrapper.style.removeProperty('transform');
        delete content.dataset.tickerWidth;
    }

    updateHeaderHeight() {
        const header = document.querySelector('header');
        const announcement = this.wrapper;
        if (!header || !announcement) return;

        const headerHeight = header.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--announcement-height', `${announcement.offsetHeight}px`);
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.swiperInitTimeout) {
            clearTimeout(this.swiperInitTimeout);
            this.swiperInitTimeout = null;
        }
        if (this.swiperInstance) {
            this.swiperInstance.destroy(true, true);
            this.swiperInstance = null;
        }

        if (this.autoplayBreakpointRemove) {
            this.autoplayBreakpointRemove();
            this.autoplayBreakpointRemove = null;
            this.autoplayBreakpointListener = null;
            this.autoplayBreakpoint = null;
        }

        if (this.contents) {
            this.contents.forEach((content) => {
                this.resetTicker(content);
            });
        }
    }

    initSwiper() {
        if (this.swiperInstance) {
            this.swiperInstance.destroy(true, true);
            this.swiperInstance = null;
        }

        if (!window.Swiper) return;

        const autoplayEnabled = this.swiperContainer.dataset.autoplay === 'true';
        const delay = parseInt(this.swiperContainer.dataset.delay, 10) || 5000;
        const mobileDelayRaw = parseInt(this.swiperContainer.dataset.mobileDelay, 10);
        const mobileDelay = Number.isNaN(mobileDelayRaw) ? delay : mobileDelayRaw;
        const mobileBreakpoint = window.matchMedia('(max-width: 767px)');
        const getCurrentDelay = () => (mobileBreakpoint.matches ? mobileDelay : delay);

        const modules = [];

        if (window.Swiper.Autoplay) {
            modules.push(window.Swiper.Autoplay);
        }

        if (window.Swiper.EffectFade) {
            modules.push(window.Swiper.EffectFade);
        }

        if (window.Swiper.A11y) {
            modules.push(window.Swiper.A11y);
        }

        if (window.Swiper.Navigation) {
            modules.push(window.Swiper.Navigation);
        }

        const options = {
            modules,
            slidesPerView: 1,
            loop: true,
            allowTouchMove: true,
            speed: 500
        };

        if (autoplayEnabled && window.Swiper.Autoplay) {
            options.autoplay = {
                delay: getCurrentDelay(),
                disableOnInteraction: false,
            };
        }

        if (window.Swiper.EffectFade) {
            options.effect = 'fade';
            options.fadeEffect = {
                crossFade: true,
            };
        }

        if (window.Swiper.Navigation) {
            options.navigation = {
                prevEl: this.swiperContainer.querySelector('.swiper-prev'),
                nextEl: this.swiperContainer.querySelector('.swiper-next'),
            };
        }

        this.swiperInstance = new window.Swiper(this.swiperContainer, options);

        this.swiperInstance.on('afterInit', this.handleSlideChange);
        this.swiperInstance.on('slideChange', this.handleSlideChange);
        this.swiperInstance.on('resize', this.handleSlideChange);

        if (this.wrapper) {
            this.resizeObserver = new ResizeObserver(this.onResize);
            this.resizeObserver.observe(this.wrapper);
        }

        if (autoplayEnabled && window.Swiper.Autoplay) {
            this.autoplayBreakpoint = mobileBreakpoint;
            this.autoplayBreakpointListener = (event) => {
                if (!this.swiperInstance || !this.swiperInstance.autoplay || !this.swiperInstance.params.autoplay) return;

                const newDelay = event.matches ? mobileDelay : delay;
                this.swiperInstance.params.autoplay.delay = newDelay;

                try {
                    this.swiperInstance.autoplay.stop();
                    this.swiperInstance.autoplay.start();
                } catch (error) {
                    console.error('AnnouncementBar: Failed to restart autoplay', error);
                }
            };

            if (mobileBreakpoint.addEventListener) {
                mobileBreakpoint.addEventListener('change', this.autoplayBreakpointListener);
                this.autoplayBreakpointRemove = () => {
                    mobileBreakpoint.removeEventListener('change', this.autoplayBreakpointListener);
                };
            } else if (mobileBreakpoint.addListener) {
                mobileBreakpoint.addListener(this.autoplayBreakpointListener);
                this.autoplayBreakpointRemove = () => {
                    mobileBreakpoint.removeListener(this.autoplayBreakpointListener);
                };
            }
        }

        this.handleSlideChange();
    }

    waitForSwiper() {
        if (this.swiperInitAttempts >= 10) return;

        this.swiperInitAttempts += 1;
        this.swiperInitTimeout = setTimeout(() => {
            this.swiperInitTimeout = null;

            if (!this.isConnected || !this.swiperContainer) return;

            if (window.Swiper) {
                this.initSwiper();
            } else {
                this.waitForSwiper();
            }
        }, 200);
    }

    handleSlideChange() {
        setTimeout(() => {
            this.updateTickers({ startActive: true });
        }, 150);
    }

    startActiveTicker() {
        const activeContents = this.getActiveContents();
        if (!activeContents.length) return;

        activeContents.forEach((content) => {
            if (!content.classList.contains('ticker')) return;

            const textWrapper = content.querySelector('.announcement-text');
            if (!textWrapper) return;

            content.classList.remove('ticker-running');
            textWrapper.style.setProperty('transform', 'translateX(0)');
            void textWrapper.offsetWidth;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!content.classList.contains('ticker')) return;
                    content.classList.add('ticker-running');
                });
            });
        });
    }

    getActiveContents() {
        if (this.swiperInstance && this.track) {
            const active = Array.from(this.track.querySelectorAll('.announcement-content.swiper-slide-active, .announcement-content.swiper-slide-duplicate-active'));
            if (active.length) {
                return active;
            }
        }

        return this.primaryContent ? [this.primaryContent] : [];
    }

    refreshContents() {
        if (!this.track) {
            this.contents = [];
            this.primaryContent = null;
            return;
        }

        this.contents = Array.from(this.track.querySelectorAll('.announcement-content'));
        this.primaryContent = this.contents.length > 0 ? this.contents[0] : null;
    }

    getTickerDuration(content) {
        if (!content) return '';

        const inlineValue = content.style.getPropertyValue('--ticker-duration');
        if (inlineValue && inlineValue.trim()) {
            return inlineValue.trim();
        }

        const computedValue = getComputedStyle(content).getPropertyValue('--ticker-duration');
        if (computedValue && computedValue.trim()) {
            return computedValue.trim();
        }

        return '';
    }
}

if (!window.customElements.get('announcement-bar')) {
    window.customElements.define('announcement-bar', AnnouncementBar);
}
